import { useState } from 'react';
import { Refused } from './credential/CredentialShell';

/**
 * Approved, and the report is the next act (docs/DECISIONS.md ZD-125 §4).
 *
 * The press is `POST /report`: the document is assembled from the released
 * findings, checked, stored and recorded, and the page re-reads `next` — so
 * what is shown afterwards is what the API said happened and never what this
 * screen assumed. A refusal is the API's sentence: not at the state a report
 * runs from, no finding released, or the product declining to ship a document
 * it found a defect in.
 */
export function ReportScreen({
  onGenerate,
}: {
  onGenerate: () => Promise<void>;
}): React.JSX.Element {
  const [pressing, setPressing] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  const press = async (): Promise<void> => {
    setRefusal(null);
    setPressing(true);
    try {
      await onGenerate();
    } catch (error) {
      setRefusal(error instanceof Error ? error.message : String(error));
    } finally {
      setPressing(false);
    }
  };

  return (
    <section className="run" aria-label="Approved">
      <h2>Approved for reporting</h2>
      <p className="run__note">
        Every finding carries a decision and the assessment is through the gate. Generating the
        report writes the document from the findings that were released — a rejected finding is kept
        on the record and left out of it — and it can be read and downloaded here once it exists.
      </p>
      {refusal === null ? null : <Refused message={refusal} />}
      <div className="run__actions">
        <button
          type="button"
          className="submit"
          disabled={pressing}
          onClick={() => {
            void press();
          }}
        >
          {pressing ? 'Generating…' : 'Generate the report'}
        </button>
      </div>
    </section>
  );
}
